import { useTranslation } from 'react-i18next';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../ui/sheet';
import { CragReportItem } from './CragReportItem';
import type { CragReport } from '../../types/crag-report';

interface CragReportsSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** What the list is about — a route's name, or the crag's. */
  title: string;
  description?: string;
  reports: CragReport[];
  /** Off when every report in the list is about the same route. */
  showClimb?: boolean;
}

/**
 * The reports behind a warning triangle, read as a list.
 *
 * A sheet rather than a tooltip or a popover: a report is a paragraph somebody
 * wrote about loose rock, and there may be three of them. It is the same sheet
 * for a route's reports and for the crag's — the only thing that changes is
 * whether each row has to name its route.
 */
export function CragReportsSheet({
  open,
  onOpenChange,
  title,
  description,
  reports,
  showClimb = false,
}: CragReportsSheetProps) {
  const { t } = useTranslation();

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[80dvh] overflow-y-auto">
        <SheetHeader className="text-left">
          <SheetTitle>{title}</SheetTitle>
          {description && <SheetDescription>{description}</SheetDescription>}
        </SheetHeader>

        {reports.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">{t('cragReports.empty')}</p>
        ) : (
          <div className="divide-y">
            {reports.map((report) => (
              <CragReportItem key={report.id} report={report} showClimb={showClimb} />
            ))}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
